import { all, dataOf, elementKey, on, ownAttr, parsePrice, text, whenVisible } from '../core/dom.js';
import { money } from '../core/money.js';

/**
 * Offer events (spec §1 offer_shown/accepted/declined and all of §4).
 *
 * Today an offer has two states: accepted, or not. That counts "never saw it" and
 * "studied it and said no" as the same thing, although one is a display problem and
 * the other a pricing problem. The missing denominator is offer_shown; the missing
 * signal is offer_abandoned — engaged rejection.
 */

export function collectOffers(tracker, { win = window, doc = document, selectors = {} } = {}) {
  const offers = new Map();

  const state = (element, index) => {
    if (!offers.has(element)) {
      offers.set(element, {
        id: offerIdOf(element, index),
        type: offerTypeOf(element),
        // Own attribute only, for the same reason as priceOf: an offer card with no sku of
        // its own must not inherit the surrounding page's.
        sku: ownAttr(element, ['data-product-sku', 'data-sku']),
        shownAt: 0,
        clicked: false,
        accepted: false,
        declined: false,
        lastInteraction: 'none',
      });
    }

    return offers.get(element);
  };

  const observe = () => {
    all(selectors.offer, doc).forEach((element, index) => {
      if (offers.has(element)) {
        return;
      }

      const current = state(element, index);

      whenVisible(
        element,
        () => {
          if (current.shownAt) {
            return;
          }
          current.shownAt = Date.now();

          tracker.track(
            'offer_shown',
            {
              offer_id: current.id,
              offer_type: current.type,
              product_sku: current.sku || undefined,
              position: index + 1,
            },
            { once: 'session', dedupeKey: 'offer_shown:' + current.id }
          );
        },
        { ratio: 0.5, win }
      );
    });
  };

  observe();

  // After-purchase and checkout offers are injected after the initial render. A cart
  // fragment replacement fires hundreds of childList records, so scans are coalesced.
  if (typeof win.MutationObserver === 'function') {
    let pending = false;

    const schedule = () => {
      if (pending) {
        return;
      }
      pending = true;

      const run = () => {
        pending = false;
        observe();
      };

      if (typeof win.requestAnimationFrame === 'function') {
        win.requestAnimationFrame(run);
      } else {
        win.setTimeout(run, 50);
      }
    };

    new win.MutationObserver(schedule).observe(doc.body || doc.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  // Offers inserted between two scans are still unknown to `offers`, so the position in
  // the current document decides their fallback id rather than a constant 0.
  const indexOf = (container) => Math.max(0, all(selectors.offer, doc).indexOf(container));

  const find = (element) => {
    const container = element.closest(selectors.offer);

    return container ? { container, data: state(container, indexOf(container)) } : null;
  };

  // CAPTURE PHASE, deliberately, for every offer delegation below.
  //
  // nf-platform's after-purchase script binds its own click handler on #nf-ap and calls
  // event.stopPropagation() for .nf-ap-accept, .nf-ap-skip and .nf-ap-complete
  // (after-purchase.js:356, :493, :548). #nf-ap sits below document, so in the bubble phase
  // the event never reaches us and every after-purchase offer event is silently lost. In
  // the capture phase we run on the way down, before that handler executes.
  const CAPTURE = true;

  on(
    doc,
    'click',
    selectors.offerAccept,
    (event, el) => {
      const found = find(el);

      if (!found) {
        return;
      }

      // The after-purchase accept button is a toggle: nf-platform flips aria-pressed to
      // "true" once the item is on the order (after-purchase.js:288), so a click while it
      // reads "true" is the customer taking the offer back off. Reporting that as a second
      // acceptance would both double-count and — because `accepted` suppresses it — hide
      // the abandonment that follows.
      if (el.getAttribute('aria-pressed') === 'true') {
        found.data.accepted = false;
        // Adding an offer and then taking it back off is the clearest engaged rejection
        // there is. bindAbandonment only reports offers that were clicked, and the accept
        // button is excluded from the offer_clicked handler, so without this the whole
        // interaction leaves no trace at all.
        found.data.clicked = true;
        found.data.lastInteraction = 'unaccept';

        return;
      }

      found.data.accepted = true;
      found.data.lastInteraction = 'accept';

      tracker.track('offer_accepted', {
        offer_id: found.data.id,
        offer_type: found.data.type,
        product_sku: found.data.sku || undefined,
        value: money(priceOf(found.container)),
        time_on_offer_s: secondsSince(found.data.shownAt),
      });
    },
    CAPTURE
  );

  on(
    doc,
    'click',
    selectors.offerDecline,
    (event, el) => {
      // `initial` / `final` / `landing` — which after-purchase step was refused.
      const phase = el.getAttribute('data-phase') || undefined;
      const found = find(el);

      if (found) {
        reportDecline(tracker, found.data, phase);

        return;
      }

      // The after-purchase container puts its skip button in a sibling .nf-ap-controls
      // section, outside every offer card, because it refuses the whole phase — which can
      // be showing more than one offer. There is no single offer to attribute it to, so
      // report one decline per offer the customer saw and did not take.
      //
      // Scoped to the enclosing #nf-ap: a side-cart drawer left open behind the
      // after-purchase page also holds tracked offers, and those were not declined.
      const scope = el.closest(selectors.offerPhaseContainer);

      offers.forEach((data, element) => {
        if (scope && !scope.contains(element)) {
          return;
        }

        reportDecline(tracker, data, phase);
      });
    },
    CAPTURE
  );

  on(
    doc,
    'click',
    selectors.offerDetails,
    (event, el) => {
      const found = find(el);

      if (!found) {
        return;
      }

      found.data.clicked = true;
      found.data.lastInteraction = 'details';

      tracker.track('offer_details_expanded', {
        offer_id: found.data.id,
        offer_type: found.data.type,
        section: text(el, 60) || 'details',
      });
    },
    CAPTURE
  );

  on(doc, 'change', selectors.offerQuantity, (event, el) => {
    const found = find(el);

    if (!found) {
      return;
    }

    const to = parseInt(el.value, 10) || 0;
    const from = parseInt(el.getAttribute('data-td-last-qty') || el.defaultValue, 10) || 0;
    el.setAttribute('data-td-last-qty', String(to));

    found.data.clicked = true;
    found.data.lastInteraction = 'quantity';

    tracker.track('offer_quantity_changed', {
      offer_id: found.data.id,
      offer_type: found.data.type,
      qty_from: from,
      qty_to: to,
    });
  });

  on(doc, 'change', selectors.offerOption, (event, el) => {
    const found = find(el);

    if (!found) {
      return;
    }

    found.data.clicked = true;
    found.data.lastInteraction = 'option';

    tracker.track('offer_option_selected', {
      offer_id: found.data.id,
      offer_type: found.data.type,
      option_id: String(el.value || dataOf(el, ['data-option-id'], '') || ''),
      option_price: money(priceOf(el.closest(selectors.offer))),
      position: Number(el.getAttribute('data-position') || 0) || undefined,
    });
  });

  // Any other click inside a shown offer counts as engagement without acceptance.
  on(
    doc,
    'click',
    selectors.offer,
    (event, container) => {
      const data = state(container, indexOf(container));

      if (data.accepted || data.declined) {
        return;
      }

      if (data.clicked) {
        return;
      }

      if (event.target.closest(selectors.offerAccept) || event.target.closest(selectors.offerDecline)) {
        return;
      }

      data.clicked = true;
      data.lastInteraction = 'click';

      tracker.track('offer_clicked', {
        offer_id: data.id,
        offer_type: data.type,
        click_target: clickTarget(event.target),
      });
    },
    CAPTURE
  );

  bindAbandonment(tracker, offers, win, doc);

  return offers;
}

/**
 * offer_abandoned — the key event of the group: shown AND clicked, never accepted.
 *
 * Sent on pagehide/visibility change; the SDK flushes with sendBeacon on unload, and
 * the direct fallback uses keepalive, so the request survives the navigation.
 */
export function bindAbandonment(tracker, offers, win = window, doc = document) {
  let done = false;

  const flush = () => {
    if (done) {
      return;
    }
    done = true;

    offers.forEach((data) => {
      if (!data.shownAt || data.accepted || !data.clicked) {
        return;
      }

      tracker.track(
        'offer_abandoned',
        {
          offer_id: data.id,
          offer_type: data.type,
          last_interaction: data.lastInteraction,
          time_on_offer_s: secondsSince(data.shownAt),
          declined_explicitly: data.declined,
        },
        { once: 'session', dedupeKey: 'offer_abandoned:' + data.id }
      );
    });

    if (typeof win.tdc === 'function') {
      win.tdc('flush');
    }
  };

  win.addEventListener('pagehide', flush);
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') {
      flush();

      return;
    }

    // Coming back is not the end of the visit: interactions after this point must
    // still be reported when the page is finally left.
    done = false;
  });
}

/**
 * One decline, at most once per offer. Silently skips an offer that was never shown, was
 * accepted, or has already been declined — a phase-level refusal walks every tracked offer,
 * so the guard is what keeps it to the ones actually on screen.
 */
function reportDecline(tracker, data, phase) {
  if (!data.shownAt || data.accepted || data.declined) {
    return;
  }

  data.declined = true;
  data.lastInteraction = 'decline';

  tracker.track('offer_declined', {
    offer_id: data.id,
    offer_type: data.type,
    product_sku: data.sku || undefined,
    decline_type: 'explicit',
    phase,
    time_on_offer_s: secondsSince(data.shownAt),
  });
}

function offerIdOf(element, index) {
  return elementKey(element, index);
}

/**
 * `cross` for a checkout cross-sell, `ap_landing` for the one-click upsell page, `ap`
 * for the offers inside the after-purchase flow (spec §1 offer_type).
 */
export function offerTypeOf(element) {
  const explicit = dataOf(element, ['data-td-offer-type', 'data-offer-type'], null);

  if (explicit) {
    return explicit;
  }

  const className = String(element.className || '');

  if (className.indexOf('nf-ap-landing') !== -1) {
    return 'ap_landing';
  }

  if (className.indexOf('nf-ap-') !== -1) {
    return 'ap';
  }

  return 'cross';
}

/**
 * Containers that hold a complete price, original and current together.
 */
const PRICE_BLOCK = '.nf-upsell-price, .nf-upsell-price-row, .nf-ap-offer__price, .price';

function priceOf(container) {
  if (!container) {
    return undefined;
  }

  // Own attribute only: dataOf walks up, and an offer without its own price would take the
  // enclosing landing page's data-price.
  const explicit = ownAttr(container, ['data-price', 'data-td-offer-value']);

  if (explicit) {
    return parsePrice(explicit);
  }

  // Read the whole price block rather than the first amount inside it. Every template
  // renders the struck-through original before the discounted current price
  // (upsells.php:44-47, sidecart-upsells.php:56-59) and querySelector returns document
  // order, so taking the first .amount reports the pre-discount figure — the offer would
  // look more expensive than it is. parsePrice keeps the last amount in the string, which
  // is what the customer actually pays.
  const block = container.querySelector(PRICE_BLOCK);

  if (block) {
    return parsePrice(block.textContent);
  }

  const node = container.querySelector('.amount, .woocommerce-Price-amount');

  return node ? parsePrice(node.textContent) : undefined;
}

function clickTarget(node) {
  if (!node) {
    return 'unknown';
  }

  const tag = (node.tagName || '').toLowerCase();

  if (tag === 'img') {
    return 'image';
  }

  if (tag === 'a' || tag === 'button') {
    return 'cta';
  }

  return tag || 'block';
}

function secondsSince(stamp) {
  return stamp ? Math.max(0, Math.round((Date.now() - stamp) / 1000)) : 0;
}
